import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';

const DESTINATIONS = [
  { key: 'shifts', title: 'My shifts', description: 'Available and assigned shifts' },
  { key: 'compliance', title: 'Renew documents', description: 'Renewals and compliance alerts' },
  { key: 'file', title: 'My staff file', description: 'Employment and staff information' },
  { key: 'training', title: 'Training and refreshers', description: 'Certificates, renewals and refresher requests' },
];

/**
 * Manual-only share helpers. Preparing a link never sends email or WhatsApp.
 * The server verifies the chosen tenant/staff record and issues a personal
 * one-use 7-day login link with a whitelisted post-login destination.
 */
export default function StaffDirectLinks({ employeeId, token, readOnly = false }) {
  const [busyKey, setBusyKey] = useState('');
  const [lastPrepared, setLastPrepared] = useState('');

  async function prepare(key) {
    const response = await axios.post(
      `${API_BASE}/employees/${employeeId}/staff-communications/prepare-destination`,
      { destination: key },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const data = response.data || {};
    if (!data.message || data.send_mode !== 'manual_review_required') {
      throw new Error('No safe manual message was prepared');
    }
    return data;
  }

  async function share(key, mode) {
    if (busyKey || readOnly || !employeeId || !token) return;
    // Reserve a tab while still handling the user gesture, before awaiting API.
    const tab = mode === 'whatsapp' ? window.open('about:blank', '_blank') : null;
    if (tab) tab.opener = null;
    setBusyKey(`${key}:${mode}`);
    setLastPrepared('');
    try {
      const data = await prepare(key);
      const destination = String(data.whatsapp_recipient || '').replace(/\D/g, '');
      if (mode === 'whatsapp' && destination && tab) {
        tab.location.href = `https://wa.me/${destination}?text=${encodeURIComponent(data.message)}`;
        toast.success('WhatsApp opened with the message ready. Nothing was sent.');
      } else {
        if (tab) tab.close();
        await navigator.clipboard.writeText(data.message);
        toast.success('Personal action link copied. Paste it into WhatsApp or an email.');
      }
      setLastPrepared(key);
    } catch {
      if (tab) tab.close();
      toast.error('Could not prepare that secure link. No message was sent.');
    } finally {
      setBusyKey('');
    }
  }

  return (
    <section className="rounded-xl border border-sky-200 bg-sky-50/50 p-3"
      data-testid="staff-direct-links">
      <h3 className="font-semibold text-slate-900">Send staff straight to what they need</h3>
      <p className="mt-1 text-xs leading-5 text-slate-600">
        Prepare a personal one-use link for an existing staff member.
        Copy it for WhatsApp/email or open WhatsApp yourself. Nothing is sent automatically.
      </p>
      <div className="mt-3 grid gap-2">
        {DESTINATIONS.map(item => (
          <div key={item.key} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                <p className="text-xs text-slate-600">{item.description}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={readOnly || Boolean(busyKey)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-900 disabled:opacity-50"
                  onClick={() => share(item.key, 'copy')}>
                  Copy {item.title} message
                </button>
                <button type="button" disabled={readOnly || Boolean(busyKey)}
                  className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50"
                  onClick={() => share(item.key, 'whatsapp')}>
                  WhatsApp: {item.title}
                </button>
              </div>
            </div>
            {lastPrepared === item.key && (
              <p role="status" className="mt-2 text-xs text-green-800">
                Ready for you to send manually. This is not a shift offer or a work-readiness approval.
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
